// Smart Sash Admin Panel - Hood Usage Summary Widget
// Compact table for the Dashboard showing last 5 sessions

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/common';
import {
  EMPTY_HOOD_USAGE_FILTERS,
  HoodUsageSession,
} from '@/models';
import { reportService } from '@/services/api/reportService';
import {
  useGroupStore,
  useDeviceStore,
  useInstitutionStore,
  useLabStore,
  useUserStore,
} from '@/store';
import { ComplianceBadge } from './ComplianceBadge';

export const HoodUsageSummary: React.FC = () => {
  const [sessions, setSessions] = useState<HoodUsageSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSummary = async () => {
      setIsLoading(true);
      try {
        // Ensure entity stores have data for fake data generation
        const hasInstitutions = useInstitutionStore.getState().institutions.length > 0;
        const hasGroups = useGroupStore.getState().groups.length > 0;
        const hasLabs = useLabStore.getState().labs.length > 0;
        const hasUsers = useUserStore.getState().users.length > 0;
        const hasDevices = useDeviceStore.getState().devices.length > 0;

        if (!hasInstitutions || !hasGroups || !hasLabs || !hasUsers || !hasDevices) {
          await Promise.all([
            !hasInstitutions && useInstitutionStore.getState().fetchInstitutions(1, 1000),
            !hasGroups && useGroupStore.getState().fetchGroups(1, 1000),
            !hasLabs && useLabStore.getState().fetchLabs(1, 1000),
            !hasUsers && useUserStore.getState().fetchUsers(),
            !hasDevices && useDeviceStore.getState().fetchDevices(1, 1000),
          ].filter(Boolean));
          reportService.resetCache();
        }

        const response = await reportService.getHoodUsageSessions({
          filters: EMPTY_HOOD_USAGE_FILTERS,
          sort: { field: 'sessionDate', order: 'desc' },
          page: 1,
          pageSize: 5,
        });
        setSessions(response.data);
      } catch (error) {
        console.error('Failed to load hood usage summary:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSummary();
  }, []);

  return (
    <Card title="Recent Hood Sessions" className="border-0 shadow-sm">
      {isLoading ? (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <span className="ms-2 text-muted">Loading...</span>
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-muted mb-0">No session data available.</p>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Hood ID</th>
                  <th>Type</th>
                  <th>User</th>
                  <th>Duration</th>
                  <th>Compliance</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td className="fw-medium">{s.hoodId}</td>
                    <td>
                      <span className={`badge bg-${s.hoodType === 'VAV' ? 'info' : 'secondary'}`}>
                        {s.hoodType}
                      </span>
                    </td>
                    <td>{s.userName}</td>
                    <td>{s.duration} min</td>
                    <td>
                      <ComplianceBadge score={s.complianceScore} level={s.complianceLevel} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-end">
            <Link to="/reports/hood-usage" className="btn btn-outline-primary btn-sm">
              View Full Report <i className="fas fa-arrow-right ms-1"></i>
            </Link>
          </div>
        </>
      )}
    </Card>
  );
};
